import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { LEVEL_META, LEVEL_LABEL } from "@/lib/constants";
import { ArrowRight, Award, Lock, Download, Activity, CheckCircle2, Layers, Share2, Linkedin, Twitter, Link2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { EmailVerifyBanner } from "@/components/EmailVerifyBanner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { linkedInAddUrl, twitterShareUrl, copyVerifyLink, openShare } from "@/lib/share";

const DASH_BG = "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg?auto=compress&cs=tinysrgb&w=1600";
const CYAN = "text-[#0891b2] dark:text-[#00E5FF]";
const LEVEL_NUM = {
  beginner: "text-[#0891b2] dark:text-[#00E5FF]",
  intermediate: "text-amber-500 dark:text-amber-300",
  advanced: "text-[#FF3B30]",
  developer: "text-indigo-500 dark:text-indigo-300",
};

function Bar({ value, accent = "#FF3B30", h = "h-1.5" }) {
  return (
    <div className={`w-full ${h} bg-zinc-200 dark:bg-white/10 overflow-hidden`}>
      <div className={`${h} transition-all duration-500`} style={{ width: `${value}%`, backgroundColor: accent }} />
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [progress, setProgress] = useState(null);
  const [labs, setLabs] = useState([]);
  const [certs, setCerts] = useState([]);

  useEffect(() => {
    (async () => {
      const [p, l, c] = await Promise.all([
        api.get("/progress"),
        api.get("/labs"),
        api.get("/certificates"),
      ]);
      setProgress(p.data);
      setLabs(l.data.labs);
      setCerts(c.data.certificates);
    })();
  }, []);

  const nextLab = labs.find((l) => l.status !== "completed" && l.is_built !== false);
  const lockedCount = labs.filter((l) => !l.is_free).length;
  const isLocked = user?.subscription_status !== "active" && user?.role !== "admin";

  return (
    <div className="bg-zinc-50 dark:bg-[#050505] min-h-screen text-zinc-900 dark:text-white transition-colors">
      {/* HEADER */}
      <div className="relative border-b border-zinc-200 dark:border-white/10 overflow-hidden">
        <img src={DASH_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-0 dark:opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-50 via-zinc-50/90 to-zinc-50/40 dark:from-[#050505] dark:via-[#050505]/90 dark:to-[#050505]/40" />
        <div className="relative max-w-7xl mx-auto px-6 py-12">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF3B30] mb-2">// Command center</div>
          <h1 className="font-display font-black uppercase tracking-tighter text-3xl sm:text-5xl">
            Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
          </h1>
          <p className="mt-2 text-xs font-mono text-zinc-500 dark:text-zinc-400">Pick up where you left off. Your range is waiting.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <EmailVerifyBanner />
        {progress && (
          <>
            {/* OVERVIEW STATS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-200 dark:bg-white/10 border border-zinc-200 dark:border-white/10 mb-8">
              <div className="bg-white dark:bg-[#0A0A0A] p-7">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Overall</span>
                  <Activity className="w-4 h-4 text-[#FF3B30]" />
                </div>
                <div className="font-display font-black text-5xl tabular-nums" data-testid="overall-progress-text">
                  {progress.overall.percent}<span className="text-2xl text-zinc-400 dark:text-zinc-600">%</span>
                </div>
                <div className="mt-4" data-testid="overall-progress-bar"><Bar value={progress.overall.percent} /></div>
                <div className="mt-2 text-[11px] font-mono text-zinc-500">{progress.overall.completed} of {progress.overall.total} labs completed</div>
              </div>
              <div className="bg-white dark:bg-[#0A0A0A] p-7">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Captured</span>
                  <CheckCircle2 className={`w-4 h-4 ${CYAN}`} />
                </div>
                <div className={`font-display font-black text-5xl tabular-nums ${CYAN}`}>{progress.overall.completed}</div>
                <div className="mt-6 text-[11px] font-mono text-zinc-500">flags captured</div>
              </div>
              <div className="bg-white dark:bg-[#0A0A0A] p-7">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">Certificates</span>
                  <Award className="w-4 h-4 text-amber-500 dark:text-amber-300" />
                </div>
                <div className="font-display font-black text-5xl tabular-nums text-amber-500 dark:text-amber-300">{certs.length}</div>
                <div className="mt-6 text-[11px] font-mono text-zinc-500">levels certified</div>
              </div>
            </div>

            {/* PER LEVEL */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 dark:bg-white/10 border border-zinc-200 dark:border-white/10 mb-10">
              {["beginner", "intermediate", "advanced", "developer", "supplychain", "infra", "claudecode101"].map((lvl) => {
                const p = progress[lvl];
                if (!p) return null;
                const meta = LEVEL_META[lvl];
                return (
                  <div key={lvl} data-testid={`level-card-${lvl}`} className="bg-white dark:bg-[#0A0A0A] p-6 group hover:bg-zinc-50 dark:hover:bg-[#101010] transition-colors">
                    <div className="flex items-center justify-between mb-4">
                      <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${meta.chipThemed}`}>{meta.label}</span>
                      <span className="font-mono text-xs text-zinc-500">{p.completed}/{p.total}</span>
                    </div>
                    <div className={`font-display font-black text-4xl tabular-nums mb-4 ${LEVEL_NUM[lvl]}`}>{p.percent}%</div>
                    <Bar value={p.percent} accent={meta.accent} h="h-1" />
                    <Link to={`/labs?level=${lvl}`}>
                      <Button variant="link" className="p-0 h-auto mt-5 text-xs font-mono uppercase tracking-wider text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white" data-testid={`view-level-${lvl}`}>
                        View labs <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </Link>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* CONTINUE LEARNING */}
        {nextLab && (
          <div className="relative border border-[#FF3B30]/40 bg-white dark:bg-[#0A0A0A] p-5 sm:p-8 mb-10 grid md:grid-cols-3 gap-6 items-center overflow-hidden">
            <div className="absolute inset-0 tactical-grid opacity-40 pointer-events-none" />
            <div className="md:col-span-2 relative">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF3B30] mb-2">// Recommended next</div>
              <h2 className="font-display font-black uppercase tracking-tighter text-2xl md:text-3xl">{nextLab.title}</h2>
              <p className="text-xs font-mono text-zinc-600 dark:text-zinc-400 mt-2 max-w-xl leading-relaxed line-clamp-2">{nextLab.description}</p>
            </div>
            <div className="relative md:text-right">
              <Link to={`/labs/${nextLab.slug}`}>
                <Button size="lg" data-testid="dashboard-next-lab-button"
                  className="rounded-none bg-[#FF3B30] text-white font-mono text-sm uppercase tracking-wider hover:bg-zinc-900 dark:hover:bg-white dark:hover:text-black active:scale-95 transition-all">
                  Resume lab <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* CERTIFICATES */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-5">
            <Layers className="w-4 h-4 text-zinc-500" />
            <h2 className="font-display font-bold uppercase tracking-tight text-xl">Your certificates</h2>
          </div>
          {certs.length === 0 ? (
            <div className="border border-dashed border-zinc-300 dark:border-white/15 p-10 text-center font-mono text-xs text-zinc-500">
              <Award className="w-7 h-7 mx-auto mb-3 text-zinc-400 dark:text-zinc-600" />
              Complete every lab in a level to earn its certificate.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 dark:bg-white/10 border border-zinc-200 dark:border-white/10">
              {certs.map((c) => (
                <div key={c.id} className="bg-white dark:bg-[#0A0A0A] p-6 hover:bg-zinc-50 dark:hover:bg-[#101010] transition-colors" data-testid={`cert-${c.verification_id}`}>
                  <Award className="w-6 h-6 text-amber-500 dark:text-amber-300 mb-3" />
                  <div className="font-display font-bold tracking-tight text-lg">{LEVEL_LABEL[c.level] || c.level} — Certificate</div>
                  <div className="font-mono text-[11px] text-zinc-500 mt-1 break-all">{c.verification_id}</div>
                  <div className="mt-4 flex items-center gap-2">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/certificates/${c.verification_id}/download`} className="flex-1">
                      <Button size="sm" data-testid={`cert-download-${c.verification_id}`}
                        className="w-full rounded-none bg-transparent border border-zinc-300 dark:border-white/20 text-zinc-900 dark:text-white font-mono text-xs uppercase tracking-wider hover:bg-zinc-900 hover:text-white dark:hover:bg-white dark:hover:text-black">
                        <Download className="w-3.5 h-3.5 mr-2" /> PDF
                      </Button>
                    </a>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" data-testid={`cert-share-${c.verification_id}`}
                          className="rounded-none bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white font-mono text-xs uppercase tracking-wider">
                          <Share2 className="w-3.5 h-3.5 mr-2" /> Share
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-none">
                        <DropdownMenuItem onClick={() => openShare(linkedInAddUrl(c))} data-testid={`cert-share-linkedin-${c.verification_id}`}>
                          <Linkedin className="w-4 h-4 mr-2 text-[#0A66C2]" /> Add to LinkedIn profile
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openShare(twitterShareUrl(c))} data-testid={`cert-share-x-${c.verification_id}`}>
                          <Twitter className="w-4 h-4 mr-2" /> Share on X
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => copyVerifyLink(c.verification_id)} data-testid={`cert-share-copy-${c.verification_id}`}>
                          <Link2 className="w-4 h-4 mr-2" /> Copy verify link
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild data-testid={`cert-view-${c.verification_id}`}>
                          <Link to={`/verify/${c.verification_id}`}>
                            <ShieldCheck className="w-4 h-4 mr-2 text-emerald-600" /> View public page
                          </Link>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* LOCKED NOTICE */}
        {isLocked && (
          <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] p-6 flex items-start gap-4">
            <Lock className="w-6 h-6 text-[#FF3B30] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-display font-bold uppercase tracking-tight text-zinc-900 dark:text-white">Unlock the full curriculum</div>
              <p className="text-xs font-mono text-zinc-600 dark:text-zinc-300 mt-1.5 leading-relaxed">
                You have free access to {labs.filter((l) => l.is_free).length} labs. Subscribe to unlock the remaining {lockedCount} labs across all three levels.
              </p>
            </div>
            <Link to="/subscription">
              <Button data-testid="dashboard-subscribe-cta"
                className="rounded-none bg-[#FF3B30] text-white font-mono text-xs uppercase tracking-wider hover:bg-zinc-900 dark:hover:bg-white dark:hover:text-black">
                Subscribe
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
